/* eslint-disable react/no-unescaped-entities */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, MapPin, Home, Award, Briefcase, Clock, Heart, Sparkles, ArrowRight } from 'lucide-react';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };

/**
 * ConvSellerJourney — Iter171 (Sprint A.4)
 * Porte d'entrée publique vers le moteur de recherche /recherche.
 * Capture la requête vendeur et redirige avec query params.
 */
export const ConvSellerJourney = () => {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');

    const criteria = [
        { icon: MapPin, label: 'Localisation' },
        { icon: Home, label: 'Type de bien' },
        { icon: Award, label: 'Spécialité' },
        { icon: Briefcase, label: 'Expérience' },
        { icon: Clock, label: 'Qualité du suivi' },
        { icon: Heart, label: 'Vos attentes' },
    ];

    const handleSubmit = (e) => {
        e.preventDefault();
        const params = new URLSearchParams({ source: 'convsellerjourney' });
        if (query.trim()) params.set('q', query.trim());
        navigate(`/recherche?${params.toString()}`);
    };

    return (
        <section className="relative py-20 md:py-28 border-t border-white/5" data-testid="seller-journey-section">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                <div className="max-w-3xl">
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF914D]/30 bg-[#FF914D]/8 text-[10px] font-medium tracking-widest text-[#FF914D] uppercase"
                          style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        <Sparkles className="w-3 h-3" />
                        Pour les vendeurs · Nouvelle génération
                    </span>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.6 }}
                        className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white leading-[1.1]"
                        style={DISPLAY}
                    >
                        Trouvez l'agent{' '}
                        <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                            qui correspond réellement à votre projet.
                        </span>
                    </motion.h2>
                    <p className="mt-5 text-base md:text-lg text-[#A19DAB] leading-relaxed">
                        Un moteur de recherche pensé pour les particuliers vendeurs.
                        Fini la roulette de l'aveugle et le premier contact frustrant —
                        l'expérience vendeur que l'immobilier mérite.
                    </p>
                </div>

                {/* Moteur de recherche fonctionnel — porte d'entrée /recherche */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.6 }}
                    className="mt-12 max-w-3xl mx-auto"
                >
                    <form
                        onSubmit={handleSubmit}
                        className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-5 md:p-6"
                        data-testid="seller-journey-form"
                    >
                        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.06] border border-white/10 focus-within:border-[#A855F7]/50 transition-colors">
                            <Search className="w-5 h-5 text-[#A19DAB] flex-shrink-0" />
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Quel agent pour vendre mon appartement à Lyon 6e ?"
                                className="flex-1 bg-transparent text-sm md:text-base text-white placeholder:text-[#6E6A7A] outline-none"
                                data-testid="seller-search-input"
                            />
                            <button
                                type="submit"
                                className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-[#A855F7] to-[#FF914D] text-white text-xs font-semibold hover:opacity-95"
                                data-testid="seller-search-submit"
                            >
                                Rechercher <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        <div className="mt-5 flex flex-wrap gap-2 justify-center">
                            {criteria.map(({ icon: Icon, label }) => (
                                <span key={label}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-xs text-[#A19DAB] bg-white/[0.02]">
                                    <Icon className="w-3.5 h-3.5 text-[#A855F7]" />
                                    {label}
                                </span>
                            ))}
                        </div>
                        {/* Mobile CTA */}
                        <button
                            type="submit"
                            className="sm:hidden mt-5 w-full inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-xl bg-gradient-to-r from-[#A855F7] to-[#FF914D] text-white text-sm font-semibold"
                            data-testid="seller-search-submit-mobile"
                        >
                            Lancer la recherche <ArrowRight className="w-4 h-4" />
                        </button>
                        <p className="text-center text-xs text-[#6E6A7A] mt-5">
                            Recherche par localisation, type de bien, spécialité, expérience,
                            qualité du suivi et vos propres attentes.
                            <br />
                            <span className="italic text-[#A19DAB]">Classement transparent. Aucun rang payant.</span>
                        </p>
                    </form>
                </motion.div>
            </div>
        </section>
    );
};
